import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, Pencil, X, Building2, Mail, Phone,
  KeyRound, ExternalLink, Copy, Eye, EyeOff, ShieldCheck,
} from "lucide-react";
import {
  inventoryListSuppliers, inventorySaveSupplier, inventoryDeleteSupplier,
  inventoryRevealSupplierPortal,
} from "../../../lib/inventory";
import { getAdminUser } from "../../../lib/admin";

// Safely turn an API error (string OR FastAPI 422 array of {msg,...}) into a user-readable string.
const parseApiError = (e, fallback = "Save failed") => {
  const detail = e?.response?.data?.detail;
  if (!detail) return e?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ");
  if (typeof detail === "object") return detail?.msg || JSON.stringify(detail);
  return String(detail);
};

const fmtDateTime = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return null; }
};

const SupplierForm = ({ initial, onSave, onCancel, busy, isOwner }) => {
  const [form, setForm] = useState({
    name: initial?.name || "",
    contact_email: initial?.contact_email || "",
    contact_phone: initial?.contact_phone || "",
    notes: initial?.notes || "",
    active: initial?.active !== false,
    portal_url: initial?.portal_url || "",
    portal_username: initial?.portal_username || "",
    portal_password: "",
  });
  const [showPw, setShowPw] = useState(false);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      className="p-4 rounded-sm grid gap-3 sm:grid-cols-2"
      style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}
      data-testid="supplier-form"
    >
      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Supplier name</span>
        <input required value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="supplier-name" placeholder="e.g. Veritiv, Dunbar Sash, Avery Dennison" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Contact email</span>
        <input type="email" value={form.contact_email} onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" data-testid="supplier-email" placeholder="orders@supplier.com" />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Contact phone</span>
        <input value={form.contact_phone} onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" placeholder="(555) 555-5555" />
      </label>
      <label className="block sm:col-span-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Notes</span>
        <textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" placeholder="Net 30, account #..." />
      </label>

      {/* Portal-login section */}
      <div className="sm:col-span-2 border-t border-[color:var(--ups-border)] pt-3 mt-1">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)] flex items-center gap-1.5 mb-1">
          <KeyRound size={12}/> Portal login (optional)
        </div>
        <p className="text-[10px] text-[color:var(--ups-subtext)] mb-3">
          Store the ordering-portal URL + credentials here. Chrome&apos;s security prevents auto-fill on a different site, but the Suppliers row will <b>open the portal AND copy the password to your clipboard</b> — paste with Ctrl+V (or ⌘V) on the login page.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Portal URL</span>
            <input type="url" value={form.portal_url}
              onChange={(e) => setForm((p) => ({ ...p, portal_url: e.target.value }))}
              className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono"
              placeholder="https://supplier.com/login"
              data-testid="supplier-portal-url" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Portal username / email</span>
            <input value={form.portal_username}
              onChange={(e) => setForm((p) => ({ ...p, portal_username: e.target.value }))}
              className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono"
              autoComplete="off"
              data-testid="supplier-portal-username" />
          </label>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">
              Portal password {initial?.has_portal_password && <span className="normal-case font-normal text-[9px]">— leave blank to keep current</span>}
            </span>
            <div className="flex items-center gap-2 mt-1">
              <input type={showPw ? "text" : "password"}
                value={form.portal_password}
                onChange={(e) => setForm((p) => ({ ...p, portal_password: e.target.value }))}
                className="input-swiss w-full px-3 py-2 rounded-sm text-sm font-mono"
                autoComplete="new-password"
                data-testid="supplier-portal-password" />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                className="p-2 rounded-sm border border-[color:var(--ups-border)]" aria-label="toggle password">
                {showPw ? <EyeOff size={13}/> : <Eye size={13}/>}
              </button>
            </div>
          </label>
        </div>
        {!isOwner && (form.portal_password || initial?.has_portal_password) && (
          <div className="text-[10px] text-[color:var(--ups-subtext)] mt-2 flex items-center gap-1.5">
            <ShieldCheck size={11}/> Passwords are encrypted at rest. Only the Owner role can reveal them.
          </div>
        )}
      </div>

      <label className="sm:col-span-2 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />
        <span>Active (uncheck to hide from material dropdowns without deleting)</span>
      </label>
      <div className="sm:col-span-2 flex gap-2 justify-end">
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-3 py-2 text-[12px] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>
            <X size={12} className="inline mr-1" />Cancel
          </button>
        )}
        <button type="submit" disabled={busy}
          className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]" data-testid="supplier-save">
          {busy ? <Loader2 size={12} className="animate-spin" /> : (initial?.id ? "Update" : "Add Supplier")}
        </button>
      </div>
    </form>
  );
};

const SuppliersTab = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const currentUser = getAdminUser();
  const isOwner = currentUser?.role === "owner";

  const reload = async () => {
    setLoading(true);
    try { setList(await inventoryListSuppliers()); }
    catch (e) { toast.error(parseApiError(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      const cleaned = {
        ...payload,
        contact_email: (payload.contact_email || "").trim() || null,
        contact_phone: (payload.contact_phone || "").trim() || null,
        notes: (payload.notes || "").trim() || null,
        portal_url: (payload.portal_url || "").trim() || null,
        portal_username: (payload.portal_username || "").trim() || null,
        portal_password: payload.portal_password || null,
      };
      await inventorySaveSupplier(editing?.id, cleaned);
      toast.success(editing?.id ? "Updated" : "Supplier added");
      setEditing(null);
      await reload();
    } catch (e) { toast.error(parseApiError(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Remove ${s.name}? Materials referencing this supplier will be detached.`)) return;
    try { await inventoryDeleteSupplier(s.id); toast.success("Removed"); await reload(); }
    catch (e) { toast.error(parseApiError(e, "Delete failed")); }
  };

  /* Open portal + copy password. Clipboard.writeText MUST run before
     window.open, otherwise Chrome revokes clipboard permission when focus
     shifts to the new tab. */
  const handleOpenPortal = async (s) => {
    if (!s.portal_url) {
      toast.error("No portal URL set");
      return;
    }
    setBusyRow(s.id);
    try {
      if (s.has_portal_password && isOwner) {
        try {
          const revealed = await inventoryRevealSupplierPortal(s.id);
          try {
            await navigator.clipboard.writeText(revealed.password);
            toast.success(`Password copied · opening ${s.name}. Paste with Ctrl+V (or ⌘V).`, { duration: 5000 });
          } catch {
            toast.warning("Password revealed but clipboard blocked by browser — try again after clicking the page.");
          }
        } catch (e) {
          toast.error(parseApiError(e, "Reveal failed"));
        }
      } else if (s.has_portal_password && !isOwner) {
        toast.info(`Opening ${s.name} — passwords can only be auto-copied by the Owner role.`);
      } else {
        toast.info(`Opening ${s.name} — no password saved yet.`);
      }
      window.open(s.portal_url, "_blank", "noopener,noreferrer");
      await reload();
    } finally { setBusyRow(null); }
  };

  const handleCopyUsername = async (s) => {
    try {
      await navigator.clipboard.writeText(s.portal_username || "");
      toast.success("Username copied");
    } catch { toast.error("Clipboard blocked"); }
  };

  const handleCopyPassword = async (s) => {
    if (!isOwner) { toast.error("Only the Owner role can copy passwords"); return; }
    setBusyRow(s.id);
    try {
      const revealed = await inventoryRevealSupplierPortal(s.id);
      await navigator.clipboard.writeText(revealed.password);
      toast.success("Password copied");
      await reload();
    } catch (e) { toast.error(parseApiError(e, "Reveal failed")); }
    finally { setBusyRow(null); }
  };

  if (loading) return <div className="p-10 text-[color:var(--ups-subtext)] flex items-center"><Loader2 className="animate-spin mr-2" size={16} />Loading…</div>;

  return (
    <div className="space-y-5" data-testid="suppliers-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl font-bold flex items-center gap-2"><Building2 size={20} /> Suppliers</h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            Approved vendors + optional portal-login vault. Passwords are encrypted at rest; only the Owner role can reveal them.
          </p>
        </div>
        {!editing && (
          <button onClick={() => setEditing({})} className="btn-brutal px-3 py-2 rounded-sm flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em]" data-testid="add-supplier-btn">
            <Plus size={14} /> Add Supplier
          </button>
        )}
      </div>

      {editing && <SupplierForm initial={editing} busy={busy} onSave={handleSave} onCancel={() => setEditing(null)} isOwner={isOwner} />}

      <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }}>
        {list.length === 0 && <div className="p-6 text-sm text-[color:var(--ups-subtext)]">No suppliers yet — add your first one.</div>}
        {list.map((s, i) => {
          const lastRevealed = fmtDateTime(s.portal_last_revealed_at);
          return (
            <div key={s.id} className="flex items-start justify-between gap-3 p-4" style={{ borderTop: i === 0 ? "none" : "1px solid var(--ups-border)" }} data-testid={`supplier-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-base">{s.name}</span>
                  {!s.active && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]" style={{ background: "#FEE2E2", color: "#991B1B" }}>Disabled</span>}
                  {s.portal_url && (
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] flex items-center gap-1"
                      style={{ background: s.has_portal_password ? "#DCFCE7" : "#FEF3C7", color: s.has_portal_password ? "#166534" : "#92400E" }}>
                      <KeyRound size={9}/> {s.has_portal_password ? "Portal Ready" : "URL Only"}
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-3 text-[12px] font-mono text-[color:var(--ups-subtext)]">
                  {s.contact_email && <span><Mail size={11} className="inline mr-1" />{s.contact_email}</span>}
                  {s.contact_phone && <span><Phone size={11} className="inline mr-1" />{s.contact_phone}</span>}
                  {s.portal_url && (
                    <span className="truncate max-w-[280px]" title={s.portal_url}>
                      <ExternalLink size={11} className="inline mr-1"/>{s.portal_url}
                    </span>
                  )}
                </div>
                {s.portal_username && (
                  <div className="mt-1 text-[12px] font-mono text-stone-700 flex items-center gap-2">
                    <span>{s.portal_username}</span>
                    <button onClick={() => handleCopyUsername(s)} className="p-0.5 hover:bg-[color:var(--ups-muted)] rounded"
                      title="Copy username" data-testid={`supplier-copy-user-${s.id}`}>
                      <Copy size={11}/>
                    </button>
                    {s.has_portal_password && (
                      <>
                        <span className="text-[color:var(--ups-subtext)]">·</span>
                        <span className="text-[color:var(--ups-subtext)]">password: ••••••••</span>
                        {isOwner && (
                          <button onClick={() => handleCopyPassword(s)} disabled={busyRow === s.id}
                            className="p-0.5 hover:bg-[color:var(--ups-muted)] rounded disabled:opacity-40"
                            title="Copy password" data-testid={`supplier-copy-pw-${s.id}`}>
                            {busyRow === s.id ? <Loader2 size={11} className="animate-spin"/> : <Copy size={11}/>}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
                {s.notes && <div className="text-[12px] mt-1 text-stone-700 whitespace-pre-wrap">{s.notes}</div>}
                {lastRevealed && <div className="text-[10px] text-[color:var(--ups-subtext)] mt-1">Portal last used: {lastRevealed}</div>}
              </div>
              <div className="flex gap-1 shrink-0">
                {s.portal_url && (
                  <button onClick={() => handleOpenPortal(s)} disabled={busyRow === s.id}
                    className="btn-brutal px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 disabled:opacity-40"
                    title="Open portal + copy password" data-testid={`supplier-open-portal-${s.id}`}>
                    {busyRow === s.id ? <Loader2 size={11} className="animate-spin"/> : <ExternalLink size={11}/>}
                    Open{s.has_portal_password && isOwner ? " + Copy" : ""}
                  </button>
                )}
                <button onClick={() => setEditing(s)} className="p-1.5 rounded-sm hover:bg-[color:var(--ups-muted)]" aria-label="Edit" data-testid={`supplier-edit-${s.id}`}><Pencil size={13} /></button>
                <button onClick={() => handleDelete(s)} className="p-1.5 rounded-sm hover:bg-[#FEE2E2]" aria-label="Remove" data-testid={`supplier-delete-${s.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SuppliersTab;
